import React, { useEffect, useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { nacAPI } from '@/lib/api';
import { cn, formatDate } from '@/lib/utils';
import { 
  Lock, Wifi, User, Shield, AlertTriangle, RefreshCw, 
  Monitor, Clock, Network, Copy, Ban, CheckCircle, Eye,
  Zap, Settings, Globe, Laptop, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const getDeviceIcon = (hostname) => {
  if (hostname?.includes('laptop')) return Laptop;
  if (hostname?.includes('printer')) return Settings;
  return Monitor;
};

export function NACPage() {
  const [devices, setDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState(null);

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    try {
      const response = await nacAPI.getDevices();
      setDevices(response.data.devices);
    } catch (error) {
      console.error('Error loading NAC devices:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleDeviceAction = async (deviceId, action) => {
    try {
      await nacAPI.deviceAction(deviceId, action);
      toast.success(`Ação "${action}" executada com sucesso!`);
      loadDevices();
      // Update selected device status locally
      if (action === 'quarantine') setSelectedDevice(prev => prev ? { ...prev, status: 'quarantine', vlan: 999 } : null);
      if (action === 'authorize') setSelectedDevice(prev => prev ? { ...prev, status: 'authenticated', vlan: 10 } : null);
      if (action === 'block') setSelectedDevice(prev => prev ? { ...prev, status: 'blocked', vlan: 0 } : null);
    } catch (error) {
      toast.error('Erro ao executar ação');
    }
  };

  const stats = {
    authenticated: devices.filter(d => d.status === 'authenticated').length,
    quarantine: devices.filter(d => d.status === 'quarantine').length,
    guest: devices.filter(d => d.status === 'guest').length,
    total: devices.length,
  };

  const getStatusColor = (status) => {
    if (status === 'authenticated') return 'bg-success/20 text-success border-success/30';
    if (status === 'quarantine') return 'bg-destructive/20 text-destructive border-destructive/30';
    return 'bg-warning/20 text-warning border-warning/30';
  };

  const getStatusIcon = (status) => {
    if (status === 'authenticated') return CheckCircle;
    if (status === 'quarantine') return Ban;
    return AlertTriangle;
  };

  // Suggested actions based on device status
  const getSuggestedActions = (device) => {
    if (device.status === 'quarantine') {
      return [
        { title: 'Investigar dispositivo', description: 'Verificar se é um dispositivo legítimo', action: 'investigate', icon: Eye },
        { title: 'Autorizar dispositivo', description: 'Mover para VLAN de produção', action: 'authorize', icon: CheckCircle },
        { title: 'Bloquear permanentemente', description: 'Adicionar à blacklist', action: 'block', icon: Ban },
        { title: 'Executar scan de vulnerabilidade', description: 'Verificar segurança do dispositivo', action: 'scan', icon: Shield },
      ];
    } else if (device.status === 'authenticated') {
      return [
        { title: 'Revogar acesso', description: 'Desconectar dispositivo da rede', action: 'revoke', icon: Ban },
        { title: 'Mover para quarentena', description: 'Isolar para investigação', action: 'quarantine', icon: AlertTriangle },
        { title: 'Ver histórico de conexões', description: 'Auditoria de acesso', action: 'history', icon: Clock },
        { title: 'Alterar VLAN', description: 'Reatribuir segmento de rede', action: 'vlan', icon: Network },
      ];
    }
    return [
      { title: 'Converter para usuário', description: 'Associar a um usuário', action: 'convert', icon: User },
      { title: 'Mover para quarentena', description: 'Isolar para investigação', action: 'quarantine', icon: AlertTriangle },
    ];
  };

  return (
    <div className="min-h-screen" data-testid="nac-page">
      <Header title="Network Access Control" />
      
      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-success/10 border-success/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Authenticated</p>
                  <p className="text-3xl font-mono font-bold text-success">{stats.authenticated}</p>
                </div>
                <Shield className="h-8 w-8 text-success opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-destructive/10 border-destructive/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Quarantine</p>
                  <p className="text-3xl font-mono font-bold text-destructive">{stats.quarantine}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-destructive opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-warning/10 border-warning/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Guest</p>
                  <p className="text-3xl font-mono font-bold text-warning">{stats.guest}</p>
                </div>
                <User className="h-8 w-8 text-warning opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-info/10 border-info/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Total</p>
                  <p className="text-3xl font-mono font-bold text-info">{stats.total}</p>
                </div>
                <Wifi className="h-8 w-8 text-info opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Devices Table */}
        <Card className="bg-card/50 border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground">
              Dispositivos Conectados ({devices.length})
            </CardTitle>
            <Button variant="outline" size="sm" onClick={loadDevices}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Hostname</TableHead>
                    <TableHead>MAC Address</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Auth Type</TableHead>
                    <TableHead>VLAN</TableHead>
                    <TableHead>Conectado em</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devices.map((device) => {
                    const StatusIcon = getStatusIcon(device.status);
                    const DeviceIcon = getDeviceIcon(device.hostname);
                    return (
                      <TableRow 
                        key={device.id} 
                        className="hover:bg-secondary/50 cursor-pointer"
                        onClick={() => setSelectedDevice(device)}
                        data-testid={`nac-device-${device.id}`}
                      >
                        <TableCell>
                          <Badge className={cn("border flex items-center gap-1 w-fit", getStatusColor(device.status))}>
                            <StatusIcon className="h-3 w-3" />
                            {device.status.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <DeviceIcon className="h-4 w-4 text-muted-foreground" />
                            <span className="font-mono">{device.hostname}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{device.mac}</TableCell>
                        <TableCell>
                          {device.user ? (
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              {device.user}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">
                            {device.auth_type || 'N/A'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono">{device.vlan}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(device.connected_at)}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Device Detail Modal */}
      <Dialog open={!!selectedDevice} onOpenChange={() => setSelectedDevice(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedDevice && React.createElement(getDeviceIcon(selectedDevice.hostname), { className: "h-6 w-6 text-primary" })}
              <span className="font-mono">{selectedDevice?.hostname}</span>
              {selectedDevice && (
                <Badge className={cn("border", getStatusColor(selectedDevice.status))}>
                  {selectedDevice.status.toUpperCase()}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedDevice && (
            <Tabs defaultValue="details" className="mt-4">
              <TabsList className="bg-secondary">
                <TabsTrigger value="details">Detalhes</TabsTrigger>
                <TabsTrigger value="network">Rede</TabsTrigger>
                <TabsTrigger value="actions">Ações</TabsTrigger>
                <TabsTrigger value="history">Histórico</TabsTrigger>
              </TabsList>
              
              <ScrollArea className="h-[450px] mt-4">
                <TabsContent value="details" className="space-y-4 mt-0">
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="bg-secondary/50">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Identificação</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Hostname</span>
                            <div className="flex items-center gap-1">
                              <span className="font-mono">{selectedDevice.hostname}</span>
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copyToClipboard(selectedDevice.hostname)}>
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">MAC Address</span>
                            <div className="flex items-center gap-1">
                              <span className="font-mono text-sm">{selectedDevice.mac}</span>
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copyToClipboard(selectedDevice.mac)}>
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Status</span>
                            <Badge className={cn("border", getStatusColor(selectedDevice.status))}>
                              {selectedDevice.status.toUpperCase()}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-secondary/50">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Autenticação</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Método</span>
                            <Badge variant="outline">{selectedDevice.auth_type || 'N/A'}</Badge>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Usuário</span>
                            <span className="font-mono">{selectedDevice.user || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Conectado em</span>
                            <span className="font-mono text-xs">{formatDate(selectedDevice.connected_at)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {selectedDevice.status === 'quarantine' && (
                    <Card className="bg-destructive/10 border-destructive/30">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-destructive mb-2 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4" /> Dispositivo em Quarentena
                        </h4>
                        <p className="text-sm">
                          Este dispositivo foi isolado por não cumprir as políticas de acesso. Verifique a identidade e tome uma ação.
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {selectedDevice.status === 'authenticated' && selectedDevice.user && (
                    <Card className="bg-success/10 border-success/30">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-success mb-2 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4" /> Acesso Autorizado
                        </h4>
                        <p className="text-sm">
                          Dispositivo autenticado via {selectedDevice.auth_type} para o usuário <strong>{selectedDevice.user}</strong>.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="network" className="space-y-4 mt-0">
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3 flex items-center gap-2">
                        <Network className="h-4 w-4" /> Configuração de Rede
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between p-2 bg-background rounded">
                          <span className="text-sm text-muted-foreground">VLAN</span>
                          <span className="font-mono font-bold">{selectedDevice.vlan}</span>
                        </div>
                        <div className="flex justify-between p-2 bg-background rounded">
                          <span className="text-sm text-muted-foreground">Tipo de VLAN</span>
                          <span className="font-mono">
                            {selectedDevice.vlan === 999 ? 'Quarentena' : 
                             selectedDevice.vlan === 10 ? 'Corporativa' :
                             selectedDevice.vlan === 30 ? 'IoT/Impressoras' : 'Guest'}
                          </span>
                        </div>
                        <div className="flex justify-between p-2 bg-background rounded">
                          <span className="text-sm text-muted-foreground">Segmento</span>
                          <span className="font-mono">
                            {selectedDevice.vlan === 999 ? '192.168.99.x' : 
                             selectedDevice.vlan === 10 ? '192.168.10.x' :
                             selectedDevice.vlan === 30 ? '192.168.30.x' : '192.168.50.x'}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Políticas Aplicadas</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between p-2 bg-background rounded">
                          <span className="text-sm">Acesso à Internet</span>
                          <Badge variant={selectedDevice.vlan !== 999 ? 'default' : 'destructive'}>
                            {selectedDevice.vlan !== 999 ? 'Permitido' : 'Bloqueado'}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-background rounded">
                          <span className="text-sm">Acesso a Servidores</span>
                          <Badge variant={selectedDevice.vlan === 10 ? 'default' : 'destructive'}>
                            {selectedDevice.vlan === 10 ? 'Permitido' : 'Bloqueado'}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-background rounded">
                          <span className="text-sm">Acesso a Impressoras</span>
                          <Badge variant={selectedDevice.vlan !== 999 ? 'default' : 'destructive'}>
                            {selectedDevice.vlan !== 999 ? 'Permitido' : 'Bloqueado'}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="actions" className="space-y-4 mt-0">
                  <Card className="bg-primary/10 border-primary/30">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-primary mb-2 flex items-center gap-2">
                        <Zap className="h-4 w-4" /> Ações Disponíveis
                      </h4>
                      <p className="text-sm">
                        Selecione uma ação para executar neste dispositivo.
                      </p>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-2 gap-4">
                    {getSuggestedActions(selectedDevice).map((action, i) => {
                      const ActionIcon = action.icon;
                      return (
                        <Card key={i} className="bg-secondary/50 hover:border-primary/50 transition-colors cursor-pointer">
                          <CardContent className="pt-4">
                            <div className="flex items-start gap-3">
                              <div className="p-2 rounded bg-primary/20">
                                <ActionIcon className="h-5 w-5 text-primary" />
                              </div>
                              <div>
                                <h5 className="font-medium text-sm">{action.title}</h5>
                                <p className="text-xs text-muted-foreground mt-1">{action.description}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  <div className="flex gap-2 pt-4">
                    {selectedDevice.status === 'quarantine' ? (
                      <>
                        <Button className="flex-1 bg-success hover:bg-success/90" onClick={() => handleDeviceAction(selectedDevice.id, 'authorize')}>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Autorizar Acesso
                        </Button>
                        <Button variant="destructive" onClick={() => handleDeviceAction(selectedDevice.id, 'block')}>
                          <Ban className="h-4 w-4 mr-2" />
                          Bloquear
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button variant="outline" className="flex-1" onClick={() => handleDeviceAction(selectedDevice.id, 'quarantine')}>
                          <AlertTriangle className="h-4 w-4 mr-2" />
                          Quarentena
                        </Button>
                        <Button variant="destructive" onClick={() => handleDeviceAction(selectedDevice.id, 'block')}>
                          <Ban className="h-4 w-4 mr-2" />
                          Bloquear
                        </Button>
                      </>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="history" className="space-y-4 mt-0">
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3 flex items-center gap-2">
                        <Clock className="h-4 w-4" /> Histórico de Conexões
                      </h4>
                      <div className="space-y-3">
                        <div className="flex gap-3 p-3 bg-background rounded">
                          <div className="w-2 h-2 rounded-full bg-success mt-2" />
                          <div>
                            <p className="font-medium text-sm">Conectado</p>
                            <p className="text-xs text-muted-foreground">{formatDate(selectedDevice.connected_at)}</p>
                            <p className="text-xs mt-1">Autenticado via {selectedDevice.auth_type || 'MAB'}</p>
                          </div>
                        </div>
                        
                        {selectedDevice.status === 'quarantine' && (
                          <div className="flex gap-3 p-3 bg-destructive/10 rounded border border-destructive/30">
                            <div className="w-2 h-2 rounded-full bg-destructive mt-2" />
                            <div>
                              <p className="font-medium text-sm text-destructive">Movido para Quarentena</p>
                              <p className="text-xs text-muted-foreground">Dispositivo não autorizado</p>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex gap-3 p-3 bg-background rounded">
                          <div className="w-2 h-2 rounded-full bg-info mt-2" />
                          <div>
                            <p className="font-medium text-sm">Primeiro registro</p>
                            <p className="text-xs text-muted-foreground">Dispositivo descoberto na rede</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default NACPage;
